import React, { useMemo, useState } from 'react';
import { Database, ShieldCheck } from 'lucide-react';
import serverRead from './toolkit/serverRead';
import Button from './shared/Button';
import { useToolkitProject, ToolkitProjectHeader } from './toolkit/ToolkitProject';
import { STEP, stepIcon } from './toolkit/ToolkitFileStep';
import ToolkitCompareResults from './toolkit/ToolkitCompareResults';
import useOnPrem from './toolkit/useOnPrem';
import './OhipTransactionsPage.css';
import './ToolkitBlocksPage.css';

// Sync › project › Toolkit › OXI Mapping — the desktop's Scripts › OXI Mapping.
//
//   1. Mappings — the property's active HCONNECT OXI conversion codes (INT_CC_DETAILS) from its OPERA v5, through the
//      project's SyncConnect (catalogue key oxi-mappings).
//   2. Validate — the toolkit lambda reads each conversion type's Opera Cloud list through the project's connection and
//      checks every EXT_VALUE: valid, inactive in Cloud, or not in Cloud (toolkit configcompare.js validateMappings).
//      A type whose Cloud list could not be read is reported as not checked, never as all-invalid.
// Read-only on both sides.

const STATUSES = [
    { value: 'invalid', label: 'Not in Cloud' },
    { value: 'inactive', label: 'Inactive in Cloud' },
    { value: 'unchecked', label: 'Not checked' },
    { value: 'valid', label: 'Valid' },
];
const COLUMNS = [
    { key: 'type', label: 'Type' },
    { key: 'pmsValue', label: 'v5 value' },
    { key: 'extValue', label: 'Sent to Cloud' },
    { key: 'message', label: 'Detail' },
    { key: 'updateDate', label: 'Updated' },
];

function ToolkitOxiMappingPage() {
    const toolkit = useToolkitProject();
    const { ctx, ready } = toolkit;
    const { ask } = useOnPrem(ctx);
    const [load, setLoad] = useState({ state: STEP.IDLE, message: '', queryId: null, count: 0 });
    const [check, setCheck] = useState({ state: STEP.IDLE, message: '', result: null });
    const [type, setType] = useState('all');

    const loadMappings = async () => {
        setLoad({ state: STEP.BUSY, message: 'Asking SyncConnect for the HCONNECT mappings…', queryId: null, count: 0 });
        setCheck({ state: STEP.IDLE, message: '', result: null });
        try {
            const d = await ask('oxi-mappings');
            if (d.status !== 'RETURNED') { setLoad({ state: STEP.ERROR, message: d.error_message || `No answer yet (${d.status}).`, queryId: null, count: 0 }); return; }
            const count = d.result?.rows?.length || 0;
            setLoad({ state: count ? STEP.DONE : STEP.ERROR, queryId: d.id, count,
                message: count ? `${count} active mapping(s) on the HCONNECT interface.` : 'The property has no active HCONNECT mappings.' });
        } catch (e) {
            setLoad({ state: STEP.ERROR, message: e?.response?.data?.message || e.message, queryId: null, count: 0 });
        }
    };

    const validate = async () => {
        setCheck({ state: STEP.BUSY, message: 'Reading the Opera Cloud lists and validating…', result: null });
        try {
            // On the server (a READ job): six Opera Cloud lists, rate plans paged, can outlast API Gateway.
            const data = await serverRead(ctx, { area: 'tasks', op: 'oxi-mapping-validate', args: { queryId: load.queryId },
                screen: 'oxi-mapping', title: 'OXI mapping validation',
                onStatus: (m) => setCheck((c) => ({ ...c, message: `Reading the Opera Cloud lists and validating… ${m}` })) });
            const s = data.summary;
            setCheck({ state: s.invalid || s.unchecked ? STEP.ERROR : STEP.DONE, result: data,
                message: `${s.valid} valid, ${s.inactive} inactive, ${s.invalid} not in Cloud${s.unchecked ? `, ${s.unchecked} not checked` : ''}.` });
            setType('all');
        } catch (e) {
            setCheck({ state: STEP.ERROR, message: e?.response?.data?.message || e.message, result: null });
        }
    };

    const result = check.result;
    const rows = useMemo(() => (result ? result.rows.filter((r) => type === 'all' || r.conversionCode === type) : []), [result, type]);
    const n = (s) => rows.filter((r) => r.status === s).length;

    return (
        <div className="otx-page">
            <ToolkitProjectHeader toolkit={toolkit} title="OXI Mapping"
                subtitle="Check every HCONNECT OXI conversion code maps to a value Opera Cloud has, and has active." />

            <div className="tkb-step">
                <div className="tkb-step-head"><span className="tkb-num">1</span> Mappings from Opera v5 {stepIcon(load.state)}</div>
                <Button variant="secondary" onClick={loadMappings} disabled={!ready || load.state === STEP.BUSY}><Database className="icon" /> Load mappings</Button>
                {load.message && <p className={`tkb-msg${load.state === STEP.ERROR ? ' tkb-fail' : ''}`}>{load.message}</p>}
            </div>

            <div className="tkb-step">
                <div className="tkb-step-head"><span className="tkb-num">2</span> Validate against Opera Cloud {stepIcon(check.state)}</div>
                <Button variant="secondary" onClick={validate} disabled={!ready || !load.queryId || check.state === STEP.BUSY}><ShieldCheck className="icon" /> Validate</Button>
                {check.message && <p className={`tkb-msg${check.state === STEP.ERROR ? ' tkb-fail' : ''}`}>{check.message}</p>}
                {result?.types.filter((t) => t.error).map((t) => <p key={t.code} className="tkb-msg tkb-fail">{t.error}</p>)}
            </div>

            {result && (
                <div className="tkb-step">
                    <div className="tkb-tabs">
                        <button type="button" className={`tkb-tab${type === 'all' ? ' tkb-tab-active' : ''}`} onClick={() => setType('all')}>All ({result.rows.length})</button>
                        {result.types.map((t) => (
                            <button key={t.code} type="button" className={`tkb-tab${type === t.code ? ' tkb-tab-active' : ''}`} onClick={() => setType(t.code)}>
                                {t.label} ({result.rows.filter((r) => r.conversionCode === t.code).length})
                            </button>
                        ))}
                    </div>
                    <ToolkitCompareResults key={type} rows={rows} columns={COLUMNS} statuses={STATUSES} exportName="OXI_Mapping"
                        summary={[
                            { label: 'Mappings', value: rows.length },
                            { label: 'Valid', value: n('valid'), tone: 'ok' },
                            { label: 'Inactive in Cloud', value: n('inactive'), tone: n('inactive') ? 'warn' : undefined },
                            { label: 'Not in Cloud', value: n('invalid'), tone: n('invalid') ? 'fail' : 'ok' },
                            ...(n('unchecked') ? [{ label: 'Not checked', value: n('unchecked'), tone: 'warn' }] : []),
                        ]} />
                </div>
            )}
        </div>
    );
}

export default ToolkitOxiMappingPage;
